import { CACHE_DURATION, CACHE_MAX_ENTRIES } from '@/api/constants'
import axios from 'axios'

/**
 * Fetch JSON from the API, preserving the { data, error, loading } contract.
 *
 * Uses the native `fetch` (not axios) so callers can opt into Next.js's Data
 * Cache / ISR via `options.next`. axios bypasses that cache entirely, which is
 * why property-detail responses were re-fetched on every request. When no
 * caching options are passed, Next 15 treats the request as uncached
 * (no-store), matching the previous axios behaviour.
 *
 * @param {string} key - Label used for logging.
 * @param {string} url - API endpoint.
 * @param {{ revalidate?: number, tags?: string[] }} [options] - Next fetch cache options.
 */
export const fetchApiData = async (key, url, options = {}) => {
  let loading = true
  let error = null
  let data = null

  const { revalidate, tags } = options
  const next = {}
  if (typeof revalidate !== 'undefined') next.revalidate = revalidate
  if (tags?.length) next.tags = tags

  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
      },
      ...(Object.keys(next).length ? { next } : {}),
    })

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`)
    }

    data = await response.json()
  } catch (err) {
    console.error(`Error fetching ${key} (${url}):`, err)
    error = err?.message || 'Failed to fetch data'
  } finally {
    loading = false
  }

  return { data, error, loading }
}

// ------------------------------ISR CACHING----------------------------------------

// Simple in-memory LRU cache
const cache = new Map()

/**
 * Fetch data with ISR-like caching (client or server-safe)
 * @param {string} key - Unique cache key
 * @param {string} url - API endpoint
 * @returns {Promise<{ data: any, error: string|null, loading: boolean, fromCache: boolean }>}
 */
export const fetchWithISR = async (key, url) => {
  const now = Date.now()
  const cached = cache.get(key)

  // ✅ Return cached if fresh
  if (cached && now - cached.timestamp < CACHE_DURATION) {
    return { data: cached.data, error: null, loading: false, fromCache: true }
  }

  let data = null
  let error = null

  try {
    const response = await axios.get(url)
   
    data = response.data

    // ✅ Maintain LRU order
    cache.delete(key)
    cache.set(key, { data, timestamp: now })

    // ✅ Enforce cache size limit
    if (cache.size > CACHE_MAX_ENTRIES) {
      const oldestKey = cache.keys().next().value
      cache.delete(oldestKey)
    }
  } catch (err) {
    console.error(`❌ Error fetching ${key}:`, err)
    error = err?.response?.data?.message || `Failed to fetch ${key}`
  }

  return { data, error, loading: false, fromCache: false }
}
